import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useJob, useUpdateJob } from "@/hooks/useJobs";
import { createPipeline } from "@/api/pipeline";
import { runEvaluation } from "@/api/run";
import { createApplication } from "@/api/applications";
import { scoreTone, statusDot, CANONICAL_STATES } from "@/format";
import { cn } from "@/cn";

const TONE_BG: Record<string, string> = {
  good: "bg-emerald-500/10 text-emerald-400",
  warn: "bg-amber-500/10 text-amber-400",
  bad: "bg-red-500/10 text-red-400",
  muted: "bg-zinc-500/10 text-muted-foreground",
};

function renderMarkdown(md: string): string {
  let html = md
    .replace(/^### (.+)$/gm, "<h3 class='text-lg font-semibold text-foreground mt-6 mb-2'>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2 class='text-xl font-bold text-foreground mt-8 mb-3'>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1 class='text-2xl font-bold text-foreground mt-8 mb-4'>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`(.+?)`/g, "<code class='rounded bg-muted/30 px-1 py-0.5 text-sm font-mono'>$1</code>")
    .replace(/\[(.+?)\]\((.+?)\)/g, "<a href='$2' class='text-brand hover:underline' target='_blank' rel='noopener'>$1</a>")
    .replace(/^- (.+)$/gm, "<li class='ml-4 text-muted-foreground'>$1</li>")
    .replace(/^---$/gm, "<hr class='my-6 border-border' />")
    .replace(/\n\n/g, "</p><p class='text-muted-foreground leading-relaxed'>").replace(/\n/g, "<br />");
  return `<p class='text-muted-foreground leading-relaxed'>${html}</p>`;
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [notes, setNotes] = useState("");
  const [notesSaved, setNotesSaved] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [evaluating, setEvaluating] = useState(false);
  const [jobDescription, setJobDescription] = useState("");

  const isInbox = id?.startsWith("INBOX:");
  // If it's an inbox-style id, redirect to jobs page (old data has been migrated)
  if (isInbox) {
    return (
      <div className="mx-auto max-w-2xl">
        <Link to="/jobs" className="mb-6 inline-flex items-center gap-1 text-sm text-brand hover:underline">Back to Jobs</Link>
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">This job has been migrated. Please use the Jobs page to view it.</p>
          <Link to="/jobs" className="mt-4 inline-block text-sm text-brand hover:underline">Go to Jobs</Link>
        </div>
      </div>
    );
  }

  const isNumericAppId = id != null && /^\d+$/.test(id);
  const { data: job, isLoading } = useJob(id && !isNumericAppId ? id : undefined);

  const activeJob = job;

  const appQuery = { data: undefined, isLoading: false, error: null };

  const createPipelineMutation = useMutation({
    mutationFn: (jobId: string) => createPipeline(jobId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["pipeline"] });
      navigate(`/pipeline/${data.pipeline.id}?stage=Evaluated`);
    },
  });

  const handleEvaluate = async () => {
    if (!activeJob) return;
    setEvaluating(true);
    try {
      const input = jobDescription || activeJob.url;
      let finalText = "";
      await runEvaluation("evaluate", input, (event) => {
        if (event.type === "text") finalText += event.text;
      });

      let score = 0;
      const verdictMatch = finalText.match(/VERDICT:\s*(\d+(?:\.\d+)?)\/5/i);
      if (verdictMatch) score = parseFloat(verdictMatch[1]);

      // Create the pipeline + application
      if (activeJob.pipelineId) {
        // Already has a pipeline — just update
      } else {
        const pipelineResult = await createPipeline(activeJob.id);
        qc.invalidateQueries({ queryKey: ["pipeline"] });
        navigate(`/pipeline/${pipelineResult.pipeline.id}?stage=Evaluated`);
      }
    } catch (err) {
      console.error("Evaluation failed:", err);
    } finally {
      setEvaluating(false);
    }
  };

  const isLoading_ = isLoading || appQuery.isLoading;
  const error_ = appQuery.error;

  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/jobs" className="mb-6 inline-flex items-center gap-1 text-sm text-brand hover:underline">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        Back to Jobs
      </Link>

      {isLoading_ && <div className="flex items-center justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" /></div>}

      {error_ && !isLoading_ && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">Job not found. <Link to="/jobs" className="underline">Return to jobs</Link></p>
        </div>
      )}

      {activeJob && !isLoading_ && (
        <div className="space-y-6">
          {/* Job Header */}
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{activeJob.company}</span>
                  <span className="text-xs text-muted-foreground">|</span>
                  <span className="rounded-full bg-muted/30 px-2 py-0.5 text-xs text-muted-foreground">{activeJob.source}</span>
                </div>
                <h1 className="text-xl font-bold text-foreground">{activeJob.role}</h1>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {activeJob.score !== undefined && (
                  <span className={cn("inline-flex items-center rounded-full px-3 py-1 font-mono text-sm font-semibold", TONE_BG[scoreTone(String(activeJob.score))])}>{activeJob.score}/5</span>
                )}
              </div>
            </div>
            {activeJob.url && (
              <a href={activeJob.url} target="_blank" rel="noopener noreferrer" className="mt-3 block truncate rounded-md bg-muted/30 px-3 py-2 text-xs text-brand hover:underline">{activeJob.url}</a>
            )}

            {/* Evaluate Button */}
            {!activeJob.pipelineId && (
              <button
                onClick={handleEvaluate}
                disabled={evaluating}
                className="mt-4 w-full rounded-md bg-brand px-4 py-2.5 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
              >
                {evaluating ? "Evaluating…" : "Evaluate & Create Pipeline"}
              </button>
            )}
            {activeJob.pipelineId && (
              <Link
                to={`/pipeline/${activeJob.pipelineId}?stage=Evaluated`}
                className="mt-4 block w-full rounded-md bg-brand/10 px-4 py-2.5 text-center text-sm font-medium text-brand transition hover:bg-brand/20"
              >
                View Pipeline
              </Link>
            )}
          </div>

          {/* Job Description */}
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Job Description</h3>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              rows={6}
              placeholder="No job description saved."
              className="w-full rounded-md border border-border bg-bg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand font-mono"
            />
          </div>

          {/* Notes */}
          <div className="rounded-lg border border-border bg-card p-5">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</h3>
            <textarea
              value={activeJob.notes ?? ""}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Add notes about this job…"
              className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
        </div>
      )}
    </div>
  );
}